/**
 * API-Service für die Arenaris Booking-Anwendung
 * Implementiert alle Backend-Endpoints gemäß API-Dokumentation
 * Implementiert Caching und Fehlerbehandlung
 * Behandelt UTC-Zeitkonvertierung für deutsche Lokalzeit
 */

// Dynamische API-URL basierend auf Environment und Host
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
const isStaging = hostname === 'staging.arenarisquelle.de';

const API_BASE_URL = isLocal
  ? "http://127.0.0.1:8000/api/v1"
  : isStaging
    ? "https://api.staging.arenarisquelle.de/v1"
    : "https://api.arenarisquelle.de/v1";

console.log('API Base URL:', API_BASE_URL, 'Host:', hostname);

// Importiere Timezone-Utilities
import { parseBackendDateTime, formatDateTimeForBackend } from './timezone.js';

// Re-exportiere für Backwards-Kompatibilität
export { parseBackendDateTime, formatDateTimeForBackend };

// Cache für Verfügbarkeitsdaten pro Zeitraum
let availabilityCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten
const VENUE_ID = 1; // Standardmäßig immer Venue ID 1

/**
 * Generiert einen Cache-Key für einen Zeitraum
 * Da fromDate immer heute ist, verwenden wir den angezeigten Monat als Key
 * @param {Date} currentMonth - Aktueller angezeigter Monat
 * @returns {string} Cache-Key
 */
function getCacheKey(currentMonth) {
  const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
  return monthKey;
}

/**
 * Prüft ob der Cache für einen bestimmten Monat noch gültig ist
 * @param {Date} currentMonth - Aktueller angezeigter Monat
 * @returns {boolean} true wenn der Cache gültig ist
 */
function isCacheValid(currentMonth) {
  const cacheKey = getCacheKey(currentMonth);
  const cached = availabilityCache.get(cacheKey);
  
  if (!cached) {
    return false;
  }
  
  const now = Date.now();
  const cacheAge = now - cached.timestamp;
  
  // Cache ist nur gültig wenn er noch nicht zu alt ist UND 
  // wenn das gecachte fromDate noch das heutige Datum ist
  const today = new Date().toISOString().split('T')[0];
  const cachedFromDate = cached.fromDate;
  
  return cacheAge < CACHE_DURATION && cachedFromDate === today;
}

/**
 * Holt die Verfügbarkeitsdaten vom availability-Endpoint
 * Neue API-Struktur: /api/v1/availability?from=YYYY-MM-DD&to=YYYY-MM-DD&venue_id=1
 * Verwendet Caching um doppelte API-Aufrufe zu vermeiden
 * @param {Date} currentMonth - Aktueller angezeigter Monat
 * @returns {Promise<Array>} Array von Buchungen in normalisierter Form
 */
export async function fetchAvailability(currentMonth) {
  const cacheKey = getCacheKey(currentMonth);
  const { fromDate, toDate } = getMonthRange(currentMonth);
  
  
  // Prüfe ob gültige Cache-Daten vorhanden sind
  if (isCacheValid(currentMonth)) {
    return availabilityCache.get(cacheKey).data;
  }

  try {
    const url = `${API_BASE_URL}/availability?from=${fromDate}&to=${toDate}&venue_id=${VENUE_ID}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors', // Explizit CORS aktivieren
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'API returned error');
    }
    
    
    // Normalisiere die Daten für Backward-Kompatibilität
    const normalizedBookings = result.data.booked_periods.map(period => ({
      start: period.start,
      end: period.end,
      state: period.status === 'booked' ? 'accepted' : 'requested', // Mapping: booked -> accepted, pending -> requested
      uid: `${period.start}_${period.end}`, // Generiere UID aus Start/End
      blocktimes: []
    }));

    // Füge blocked_periods als "accepted" hinzu (belegt)
    const blockedBookings = result.data.blocked_periods.map(period => ({
      start: period.start,
      end: period.end,
      state: 'accepted', // Blocked wird als belegt behandelt
      uid: `blocked_${period.start}_${period.end}`,
      blocktimes: []
    }));

    // Kombiniere booked_periods und blocked_periods
    const allBookings = [...normalizedBookings, ...blockedBookings];
    
    // Erweitere um virtuelle Wochenend-Buchungen
    const expandedBookings = expandWeekendBookings(allBookings);
    
    // Cache die normalisierten Daten mit fromDate für Validierung
    availabilityCache.set(cacheKey, {
      data: expandedBookings,
      timestamp: Date.now(),
      fromDate: fromDate, // Für Cache-Validierung
      toDate: toDate,
      rawResponse: result
    });
    
    return expandedBookings;
  } catch (error) {
    console.error('API-Endpoint nicht erreichbar:', error.message);
    throw new Error(
      `Verfügbarkeitsdaten konnten nicht geladen werden: ${error.message}`
    );
  }
}

/**
 * Custom Error-Klasse für Backend-Validierungsfehler
 * Enthält sowohl die generische Nachricht als auch die detaillierten Feld-Fehler
 */
export class ValidationError extends Error {
  constructor(message, errors = {}) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors; // Backend-Feld-Fehler im Format { "customer.first_name": ["..."] }
  }
}

/**
 * Sendet eine neue Buchungsanfrage an den booking-Endpoint
 * Neue API-Struktur: POST /api/v1/booking
 * @param {Object} bookingData - Buchungsdaten im neuen Backend-Format
 * @returns {Promise<Object>} Antwort vom Server mit success, data, message
 * @throws {ValidationError} Bei Validierungsfehlern mit detaillierten Feld-Fehlern
 */
export async function submitBookingRequest(bookingData) {
  try {
    const response = await fetch(`${API_BASE_URL}/booking`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(bookingData)
    });

    const result = await response.json();
    
    if (!response.ok) {
      // Bei Validierungsfehlern (422) die detaillierten Fehler zurückgeben
      if (result.errors && Object.keys(result.errors).length > 0) {
        const errorMessage = result.message || 'Validierungsfehler';
        throw new ValidationError(errorMessage, result.errors);
      }
      // Bei anderen Fehlern die Nachricht verwenden
      if (result.message) {
        throw new Error(result.message);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    if (!result.success) {
      throw new Error(result.message || 'API returned error');
    }
    
    // Cache leeren, damit neue Buchung bei nächster Abfrage sichtbar wird
    clearAvailabilityCache();
    
    return result;
  } catch (error) {
    console.error('Fehler beim Senden der Buchungsanfrage:', error);
    throw error;
  }
}

/**
 * Konvertiert Frontend-Formulardaten in das neue Backend-Format
 * Mappt die Feldnamen und Strukturen gemäß neuer API-Dokumentation
 * @param {Object} formData - Formulardaten vom Frontend
 * @param {Object} selectedDateRange - Ausgewählter Datumsbereich
 * @param {number} totalPrice - Berechneter Gesamtpreis
 * @returns {Object} Buchungsdaten im neuen Backend-Format
 */
export function convertFormDataToBackendFormat(formData, selectedDateRange, totalPrice) {
  // Kombiniere Datum und Zeit für start_datetime und end_datetime
  // Arbeite mit lokaler Zeit, konvertiere dann zu UTC für Backend
  const startDateTime = new Date(selectedDateRange.start);
  const endDateTime = new Date(selectedDateRange.end);
  
  const [startHour, startMinute] = formData.start_time.split(':').map(Number);
  const [endHour, endMinute] = formData.end_time.split(':').map(Number);
  
  // Setze lokale Zeit
  startDateTime.setHours(startHour, startMinute, 0, 0);
  endDateTime.setHours(endHour, endMinute, 0, 0);
  
  // Teile den Namen in Vor- und Nachname auf
  const nameParts = formData.customer_name.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';
  
  // Bestimme den Buchungstyp basierend auf is_local_club
  const bookingType = formData.is_local_club ? 'association' : 'private';
  
  // Equipment IDs - Geschirr hat ID 1
  const equipmentIds = [];
  if (formData.needs_dishes) {
    equipmentIds.push(1);
  }
  
  // Formatiere DateTime für Backend
  const startIso = formatDateTimeForBackend(startDateTime);
  const endIso = formatDateTimeForBackend(endDateTime);
  
  return {
    customer: {
      first_name: firstName,
      last_name: lastName,
      email: formData.customer_email,
      phone: formData.customer_phone,
      organization: formData.customer_organization || '', // Neues Feld aus dem Formular
      tax_id: formData.customer_tax_id || '', // Neues Feld aus dem Formular
      address: {
        street: formData.customer_street,
        zip_code: formData.customer_zip,
        city: formData.customer_city
      }
    },
    booking_details: {
      type: bookingType,
      event_purpose: formData.event_purpose || '', // Zweck der Veranstaltung
      start_datetime: startIso,
      end_datetime: endIso,
      venue_id: VENUE_ID, // Standardmäßig immer 1
      equipment_ids: equipmentIds,
      notes: formData.notes || ''
    }
  };
}

/**
 * Löscht den Cache und zwingt einen neuen API-Aufruf
 * Nützlich für manuelle Aktualisierungen
 * @param {Date} currentMonth - Optional: nur bestimmten Monat löschen
 */
export function clearAvailabilityCache(currentMonth = null) {
  if (currentMonth) {
    const cacheKey = getCacheKey(currentMonth);
    availabilityCache.delete(cacheKey);
  } else {
    availabilityCache.clear();
  }
}

/**
 * Hilfsfunktion um den Zeitraum für die Availability-Abfrage zu bekommen
 * Startet immer vom heutigen Datum und geht bis zum Ende des nächsten Monats
 * @param {Date} currentMonth - Aktueller Monat (vom Kalender)
 * @returns {Object} { fromDate, toDate } im Format YYYY-MM-DD
 */
export function getMonthRange(currentMonth) {
  // Startdatum ist immer heute (nicht der erste Tag des angezeigten Monats)
  const today = new Date();
  const fromDate = today.toISOString().split('T')[0]; // YYYY-MM-DD
  
  // Enddatum ist der letzte Tag des nächsten Monats vom angezeigten Monat
  const endOfNextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
  const toDate = endOfNextMonth.toISOString().split('T')[0]; // YYYY-MM-DD
  
  return { fromDate, toDate };
}

/**
 * Holt den Preis für einen Buchungszeitraum vom Backend
 * API-Struktur: POST /api/v1/price mit Body
 * @param {Object} params - Parameter für die Preisabfrage
 * @param {Date} params.startDate - Startdatum
 * @param {Date} params.endDate - Enddatum
 * @param {string} params.startTime - Startzeit im Format "HH:MM"
 * @param {string} params.endTime - Endzeit im Format "HH:MM"
 * @param {boolean} params.isLocalClub - Ob es sich um einen lokalen Verein handelt (bestimmt type=association vs private)
 * @param {boolean} params.needsDishes - Ob Geschirr benötigt wird (equipment_id=1)
 * @returns {Promise<Object>} Preisberechnung vom Backend
 */
export async function fetchBookingPrice({
  startDate,
  endDate,
  startTime,
  endTime,
  isLocalClub,
  needsDishes
}) {
  try {
    // Kombiniere Datum und Zeit für start_datetime und end_datetime
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);
    
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    // Setze lokale Zeit
    startDateTime.setHours(startHour, startMinute, 0, 0);
    endDateTime.setHours(endHour, endMinute, 0, 0);
    
    // Formatiere für Backend (Berlin-Zeit ohne Zeitzone)
    const startIso = formatDateTimeForBackend(startDateTime);
    const endIso = formatDateTimeForBackend(endDateTime);
    
    // Bestimme Parameter
    const type = isLocalClub ? 'association' : 'private';
    
    // Erstelle Request Body
    const requestBody = {
      start: startIso,
      end: endIso,
      type: type,
      equipment: []
    };
    
    // Füge Geschirr hinzu wenn benötigt (equipment_id=1, quantity=1)
    if (needsDishes) {
      requestBody.equipment.push({
        equipment_id: 1,
        quantity: 1
      });
    }
    
    const url = `${API_BASE_URL}/price`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Prüfe ob die Antwort das erwartete data-Feld hat
    if (!result.data) {
      throw new Error(result.error || 'API returned no data');
    }
    
    // Konvertiere Backend-Response in Frontend-Format
    const backendData = result.data;
    
    return {
      totalPrice: backendData.total,
      currency: backendData.currency,
      details: backendData.breakdown.rules_applied || [],
      equipmentCost: backendData.breakdown.equipment_cost || 0,
      // Für Kompatibilität mit Frontend-Code
      isWeekend: false, // Wird vom Backend bestimmt
      isOvernight: startDate.getTime() !== endDate.getTime(),
      weekdayHours: 0, // Nicht mehr relevant
      weekdayBlocks: 0, // Nicht mehr relevant
      weekendDays: 0, // Nicht mehr relevant
      optimizedEndTime: null // Nicht mehr relevant
    };
    
  } catch (error) {
    console.error('Fehler beim Abrufen des Preises vom Backend:', error);
    throw error;
  }
}

/**
 * Prüft ob eine Buchung die Wochenend-Regel auslöst
 * Regel: Wenn eine Buchung am Freitag nach 8 Uhr, Samstag oder Sonntag (egal wann) vorliegt,
 * werden Freitag, Samstag und Sonntag als ganztägig gebucht/angefragt behandelt
 * @param {Date} bookingStart - Startdatum/-zeit der Buchung
 * @param {Date} bookingEnd - Enddatum/-zeit der Buchung
 * @returns {boolean} true wenn die Wochenend-Regel ausgelöst wird
 */
function triggersWeekendRule(bookingStart, bookingEnd) {
  // Prüfe ob die Buchung Freitag, Samstag oder Sonntag berührt
  const startDay = bookingStart.getDay();
  const endDay = bookingEnd.getDay();
  
  // Prüfe Samstag (6) oder Sonntag (0) - egal wann
  if (startDay === 0 || startDay === 6 || endDay === 0 || endDay === 6) {
    return true;
  }
  
  // Prüfe Freitag (5) nach 8 Uhr beim Start
  if (startDay === 5) {
    const startHour = bookingStart.getHours();
    if (startHour >= 8) {
      return true;
    }
  }
  
  // Prüfe ob die Buchung den Freitag nach 8 Uhr berührt
  // Iteriere über alle Tage der Buchung
  const current = new Date(bookingStart);
  const end = new Date(bookingEnd);
  
  // Prüfe jeden Tag im Zeitraum
  while (current <= end) {
    const day = current.getDay();
    
    // Samstag (6) oder Sonntag (0) - egal wann
    if (day === 0 || day === 6) {
      return true;
    }
    
    // Freitag (5) - prüfe ob nach 8 Uhr
    if (day === 5) {
      // Wenn die Buchung am Freitag startet, prüfe Startzeit
      if (isSameDay(current, bookingStart)) {
        const startHour = bookingStart.getHours();
        if (startHour >= 8) {
          return true;
        }
      }
      // Wenn die Buchung am Freitag endet, prüfe ob sie nach 8 Uhr geht
      else if (isSameDay(current, bookingEnd)) {
        const endHour = bookingEnd.getHours();
        if (endHour >= 8) {
          return true;
        }
      }
      // Wenn die Buchung den ganzen Freitag umfasst, ist sie nach 8 Uhr
      else {
        return true;
      }
    }
    
    // Nächster Tag
    current.setDate(current.getDate() + 1);
    current.setHours(0, 0, 0, 0);
  }
  
  return false;
}

/**
 * Prüft ob zwei Daten am selben Tag liegen
 * @param {Date} date1 - Erstes Datum
 * @param {Date} date2 - Zweites Datum
 * @returns {boolean} true wenn beide am selben Tag liegen
 */
function isSameDay(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

/**
 * Erweitert Verfügbarkeitsdaten um virtuelle Wochenend-Buchungen
 * Wenn eine Buchung die Wochenend-Regel auslöst, werden Freitag, Samstag und Sonntag
 * als ganztägig gebucht/angefragt hinzugefügt
 * @param {Array} bookings - Array von bestehenden Buchungen
 * @returns {Array} Erweiterte Buchungen mit virtuellen Wochenend-Buchungen
 */
function expandWeekendBookings(bookings) {
  const expandedBookings = [...bookings];
  const weekendBookings = new Map(); // Map für bereits hinzugefügte Wochenend-Buchungen pro Woche
  
  bookings.forEach(booking => {
    const bookingStart = parseBackendDateTime(booking.start);
    const bookingEnd = parseBackendDateTime(booking.end);
    
    if (!bookingStart || !bookingEnd) return;
    
    // Prüfe ob die Buchung die Wochenend-Regel auslöst
    if (triggersWeekendRule(bookingStart, bookingEnd)) {
      // Finde den Freitag, Samstag und Sonntag der betroffenen Woche
      // Wenn die Buchung Samstag oder Sonntag berührt, verwende diese Woche
      // Wenn die Buchung Freitag nach 8 Uhr berührt, verwende diesen Freitag
      
      let targetFriday = null;
      
      // Finde den relevanten Freitag
      const startDay = bookingStart.getDay();
      const endDay = bookingEnd.getDay();
      
      if (startDay === 5) {
        // Buchung startet am Freitag
        targetFriday = new Date(bookingStart);
        targetFriday.setHours(0, 0, 0, 0);
      } else if (startDay === 6) {
        // Buchung startet am Samstag - Freitag ist der Tag davor
        targetFriday = new Date(bookingStart);
        targetFriday.setDate(targetFriday.getDate() - 1);
        targetFriday.setHours(0, 0, 0, 0);
      } else if (startDay === 0) {
        // Buchung startet am Sonntag - Freitag ist 2 Tage davor
        targetFriday = new Date(bookingStart);
        targetFriday.setDate(targetFriday.getDate() - 2);
        targetFriday.setHours(0, 0, 0, 0);
      } else if (endDay === 5 || endDay === 6 || endDay === 0) {
        // Buchung endet am Wochenende - finde den Freitag dieser Woche
        const endDate = new Date(bookingEnd);
        endDate.setHours(0, 0, 0, 0);
        
        if (endDay === 5) {
          targetFriday = endDate;
        } else if (endDay === 6) {
          targetFriday = new Date(endDate);
          targetFriday.setDate(targetFriday.getDate() - 1);
        } else if (endDay === 0) {
          targetFriday = new Date(endDate);
          targetFriday.setDate(targetFriday.getDate() - 2);
        }
      } else {
        // Buchung berührt Freitag nach 8 Uhr - finde den Freitag
        // Iteriere über alle Tage im Zeitraum
        const current = new Date(bookingStart);
        const end = new Date(bookingEnd);
        
        while (current <= end) {
          const day = current.getDay();
          
          if (day === 5) {
            // Prüfe ob die Buchung den Freitag nach 8 Uhr berührt
            if (isSameDay(current, bookingStart)) {
              // Buchung startet am Freitag
              if (bookingStart.getHours() >= 8) {
                targetFriday = new Date(current);
                targetFriday.setHours(0, 0, 0, 0);
                break;
              }
            } else if (isSameDay(current, bookingEnd)) {
              // Buchung endet am Freitag
              if (bookingEnd.getHours() >= 8) {
                targetFriday = new Date(current);
                targetFriday.setHours(0, 0, 0, 0);
                break;
              }
            } else {
              // Buchung umfasst den ganzen Freitag
              targetFriday = new Date(current);
              targetFriday.setHours(0, 0, 0, 0);
              break;
            }
          }
          
          // Nächster Tag
          current.setDate(current.getDate() + 1);
          current.setHours(0, 0, 0, 0);
        }
      }
      
      if (targetFriday) {
        // Erstelle einen eindeutigen Key für diese Woche (Jahr-Woche)
        const weekKey = `${targetFriday.getFullYear()}-W${getWeekNumber(targetFriday)}`;
        
        // Prüfe ob für diese Woche bereits eine Wochenend-Buchung hinzugefügt wurde
        if (!weekendBookings.has(weekKey)) {
          weekendBookings.set(weekKey, true);
          
          // Erstelle virtuelle Buchungen für Freitag, Samstag und Sonntag
          const saturday = new Date(targetFriday);
          saturday.setDate(saturday.getDate() + 1);
          
          const sunday = new Date(targetFriday);
          sunday.setDate(sunday.getDate() + 2);
          
          // Erstelle ganztägige Buchungen (00:00-23:59:59)
          const fridayStart = new Date(targetFriday);
          fridayStart.setHours(0, 0, 0, 0);
          const fridayEnd = new Date(targetFriday);
          fridayEnd.setHours(23, 59, 59, 999);
          
          const saturdayStart = new Date(saturday);
          saturdayStart.setHours(0, 0, 0, 0);
          const saturdayEnd = new Date(saturday);
          saturdayEnd.setHours(23, 59, 59, 999);
          
          const sundayStart = new Date(sunday);
          sundayStart.setHours(0, 0, 0, 0);
          const sundayEnd = new Date(sunday);
          sundayEnd.setHours(23, 59, 59, 999);
          
          // Verwende den Status der ursprünglichen Buchung
          const state = booking.state;
          
          // Füge virtuelle Buchungen hinzu
          expandedBookings.push({
            start: formatDateTimeForBackend(fridayStart),
            end: formatDateTimeForBackend(fridayEnd),
            state: state,
            uid: `weekend_friday_${weekKey}_${booking.uid}`,
            blocktimes: [],
            isWeekendExpansion: true // Flag für virtuelle Buchung
          });
          
          expandedBookings.push({
            start: formatDateTimeForBackend(saturdayStart),
            end: formatDateTimeForBackend(saturdayEnd),
            state: state,
            uid: `weekend_saturday_${weekKey}_${booking.uid}`,
            blocktimes: [],
            isWeekendExpansion: true
          });
          
          expandedBookings.push({
            start: formatDateTimeForBackend(sundayStart),
            end: formatDateTimeForBackend(sundayEnd),
            state: state,
            uid: `weekend_sunday_${weekKey}_${booking.uid}`,
            blocktimes: [],
            isWeekendExpansion: true
          });
        }
      }
    }
  });
  
  return expandedBookings;
}

/**
 * Berechnet die Kalenderwoche für ein Datum
 * @param {Date} date - Datum
 * @returns {number} Kalenderwoche
 */
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Prüft ob ein bestimmter Zeitraum verfügbar ist
 * @param {Date} startDate - Startdatum
 * @param {Date} endDate - Enddatum
 * @param {Array} bookings - Array von bestehenden Buchungen
 * @returns {Object} Verfügbarkeitsstatus mit isAvailable und conflicts
 */
export function checkAvailability(startDate, endDate, bookings) {
  const conflicts = [];
  
  // Erweitere Buchungen um virtuelle Wochenend-Buchungen
  const expandedBookings = expandWeekendBookings(bookings);
  
  expandedBookings.forEach(booking => {
    // Konvertiere UTC-Zeiten vom Backend in lokale Zeit
    const bookingStart = parseBackendDateTime(booking.start);
    const bookingEnd = parseBackendDateTime(booking.end);
    
    // Überspringe ungültige Daten
    if (!bookingStart || !bookingEnd) return;
    
    // Prüfe auf Überlappungen
    if (startDate < bookingEnd && endDate > bookingStart) {
      conflicts.push({
        start: bookingStart,
        end: bookingEnd,
        state: booking.state,
        isWeekendExpansion: booking.isWeekendExpansion || false
      });
    }
  });
  
  const isAvailable = conflicts.length === 0;
  
  // Formatiere die Konflikte für die Anzeige und trenne nach Status
  const acceptedConflicts = conflicts.filter(c => c.state === 'accepted');
  const requestedConflicts = conflicts.filter(c => c.state === 'requested');
  
  const formatConflict = (conflict) => {
    const startDateStr = conflict.start.toLocaleDateString('de-DE');
    const endDateStr = conflict.end.toLocaleDateString('de-DE');
    const startTimeStr = conflict.start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const endTimeStr = conflict.end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    
    // Prüfe ob es sich um denselben Tag handelt
    if (startDateStr === endDateStr) {
      return `${startDateStr} ${startTimeStr}-${endTimeStr} Uhr`;
    } else {
      return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr} Uhr`;
    }
  };
  
  const formattedAcceptedConflicts = acceptedConflicts.map(formatConflict);
  const formattedRequestedConflicts = requestedConflicts.map(formatConflict);
  
  return {
    isAvailable,
    conflicts,
    formattedAcceptedConflicts,
    formattedRequestedConflicts,
    hasRequested: conflicts.some(c => c.state === 'requested'),
    hasAccepted: conflicts.some(c => c.state === 'accepted')
  };
}